import type { Character } from "@dm/shared";
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { Avatar } from "../Avatar.js";
import { useApp } from "../appContext.js";
import {
  ABILITIES,
  ABILITY_NAMES,
  CONDITIONS,
  HIT_DIE_SIDES,
  MAX_ATTUNEMENT,
  SKILLS,
  SKILL_KEYS,
  SKILL_NAMES,
  abilityMod,
  attackBonus,
  attunedCount,
  carryingCapacity,
  checkModifier,
  damageFormula,
  formatMod,
  newId,
  parseFormula,
  passiveScore,
  proficiencyForLevel,
  spellAttackBonus,
  spellSaveDc,
  spendSlot,
  totalLevel,
  totalWeight,
  type Ability,
  type Attack,
  type CharacterDetails,
  type CheckSpec,
  type Feature,
  type Item,
  type Recharge,
  type Skill,
  type Spell,
} from "@dm/shared";
import { HpBar, DeathSaves } from "../Hp.js";
import { ModeToggle } from "../Dice.js";
import { Field, Num, OptNum, Pips, patchItem } from "./fields.js";
import type { SheetCtx } from "./context.js";

const ALIGNMENTS = ["", "Lawful good", "Neutral good", "Chaotic good", "Lawful neutral", "True neutral", "Chaotic neutral", "Lawful evil", "Neutral evil", "Chaotic evil", "Unaligned"];
const ORD = ["Cantrips", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th"];
const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function RollBtn({ c, onClick, title, label = "🎲" }: { c: SheetCtx; onClick: () => void; title: string; label?: string }) {
  if (!c.canAct) return null;
  return (
    <button type="button" className="small-btn roll-btn" disabled={c.busy} title={title} onClick={onClick}>
      {label}
    </button>
  );
}
const checkRoll = (c: SheetCtx, check: CheckSpec) => c.roll({ check }, true);

// ------------------------------------------------------------ header
const RACES = ["Dragonborn", "Dwarf", "Elf", "Gnome", "Half-Elf", "Halfling", "Half-Orc", "Human", "Tiefling"];
const CLASSES = ["Artificer", "Barbarian", "Bard", "Cleric", "Druid", "Fighter", "Monk", "Paladin", "Ranger", "Rogue", "Sorcerer", "Warlock", "Wizard"];
const OTHER = "__other__";

/** This campaign's homebrew races and classes (approved ones), for the drop-downs. */
function useHomebrewNames(): { race: string[]; class: string[] } {
  const { me } = useApp();
  const id = me.campaign?.id;
  const [names, setNames] = useState<{ race: string[]; class: string[] }>({ race: [], class: [] });
  useEffect(() => {
    if (!id) return;
    api.homebrew(id).then((r) => {
      const ok = r.entries.filter((e) => e.status === "approved");
      setNames({ race: ok.filter((e) => e.kind === "race").map((e) => e.name), class: ok.filter((e) => e.kind === "class").map((e) => e.name) });
    }, () => undefined);
  }, [id]);
  return names;
}

/** A drop-down of standard + homebrew choices, with "Other…" for custom text. Unknown saved values stay as custom text. */
function ChoiceSelect({ value, options, homebrew, placeholder, onChange }: {
  value: string; options: string[]; homebrew: string[]; placeholder: string; onChange: (v: string) => void;
}) {
  const known = [...options, ...homebrew];
  const listed = known.some((o) => o.toLowerCase() === value.trim().toLowerCase());
  const [custom, setCustom] = useState(!!value && !listed);
  const showInput = custom || (!!value && !listed);
  const selected = showInput ? OTHER : known.find((o) => o.toLowerCase() === value.trim().toLowerCase()) ?? "";
  return (
    <span className="choice-select">
      <select value={selected} onChange={(e) => {
        if (e.target.value === OTHER) setCustom(true);
        else {
          setCustom(false);
          onChange(e.target.value);
        }
      }}>
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        {homebrew.length > 0 && (
          <optgroup label="Homebrew">
            {homebrew.filter((h) => !options.some((o) => o.toLowerCase() === h.toLowerCase())).map((o) => <option key={`hb-${o}`} value={o}>{o}</option>)}
          </optgroup>
        )}
        <option value={OTHER}>Other…</option>
      </select>
      {showInput && <input value={value} maxLength={60} placeholder={placeholder} autoFocus={custom && !value} onChange={(e) => onChange(e.target.value)} />}
    </span>
  );
}

/** Upload / replace / remove the character's avatar (owner or Host). The server re-encodes it to a 256x256 WebP. */
function AvatarPicker({ c }: { c: SheetCtx }) {
  const { me } = useApp();
  const ch = c.character;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cur, setCur] = useState<Character | null>(ch);
  useEffect(() => { setCur(ch); }, [ch?.id, ch?.avatarUrl]);
  const campaignId = me.campaign?.id;
  const shown = cur ?? ch;
  const canEdit = !!ch && !!campaignId && !c.ro && c.canAct;
  const pick = (file: File | undefined) => {
    if (!file || !ch || !campaignId) return;
    setErr(null);
    if (file.size > 2 * 1024 * 1024) return setErr("That image is over 2 MB.");
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) return setErr("Use a PNG, JPG, WebP or GIF.");
    const r = new FileReader();
    r.onload = () => {
      setBusy(true);
      api.setAvatar(campaignId, ch.id, String(r.result)).then(setCur, (e: Error) => setErr(e.message)).finally(() => setBusy(false));
    };
    r.readAsDataURL(file);
  };
  const remove = () => {
    if (!ch || !campaignId) return;
    setBusy(true);
    api.removeAvatar(campaignId, ch.id).then(setCur, (e: Error) => setErr(e.message)).finally(() => setBusy(false));
  };
  return (
    <div className="avatar-picker">
      {shown?.avatarUrl || !c.d.portraitUrl
        ? <Avatar c={shown ? { name: c.sheet.name || shown.name, avatarUrl: shown.avatarUrl } : { name: c.sheet.name, avatarUrl: null }} size={96} />
        : <img className="portrait" src={c.d.portraitUrl} alt="" referrerPolicy="no-referrer" />}
      {canEdit && (
        <div className="avatar-actions">
          <label className={`btn small${busy ? " disabled" : ""}`}>
            {busy ? "Saving…" : shown?.avatarUrl ? "Replace" : "Upload picture"}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden disabled={busy}
              onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {shown?.avatarUrl && <button className="small" disabled={busy} onClick={remove}>Remove</button>}
        </div>
      )}
      {!ch && <span className="muted small">Save the character to add a picture</span>}
      {err && <span className="error small">{err}</span>}
    </div>
  );
}

export function HeaderSection({ c }: { c: SheetCtx }) {
  const { sheet, d, update, updateDetails } = c;
  const total = totalLevel(sheet.level, d);
  const hb = useHomebrewNames();
  return (
    <div className="header-grid">
      <AvatarPicker c={c} />
      <div className="header-fields">
        <Field label="Name" className="span2">
          <input value={sheet.name} maxLength={60} onChange={(e) => update({ name: e.target.value })} placeholder="Thorin Oakenshield" />
        </Field>
        <Field label="Race">
          <ChoiceSelect value={sheet.race} options={RACES} homebrew={hb.race} placeholder="Your race" onChange={(race) => update({ race })} />
        </Field>
        <Field label="Class">
          <ChoiceSelect value={sheet.className} options={CLASSES} homebrew={hb.class} placeholder="Your class" onChange={(className) => update({ className })} />
        </Field>
        <Field label="Level">
          <Num
            value={sheet.level}
            min={1}
            max={20}
            onChange={(level) => {
              // Keep proficiency in step with (total) level unless it was customised.
              const auto = sheet.proficiencyBonus === proficiencyForLevel(totalLevel(sheet.level, d));
              update({ level, ...(auto ? { proficiencyBonus: proficiencyForLevel(totalLevel(level, d)) } : {}) });
            }}
          />
        </Field>
        <Field label="Subclass">
          <input value={d.subclass} maxLength={80} onChange={(e) => updateDetails({ subclass: e.target.value })} placeholder="Battle Master" />
        </Field>
        <Field label="Background">
          <input value={d.background} maxLength={80} onChange={(e) => updateDetails({ background: e.target.value })} placeholder="Soldier" />
        </Field>
        <Field label="Alignment">
          <select value={d.alignment} onChange={(e) => updateDetails({ alignment: e.target.value })}>
            {[...new Set([...ALIGNMENTS, d.alignment])].map((a) => (
              <option key={a} value={a}>
                {a || "—"}
              </option>
            ))}
          </select>
        </Field>
        <Field label="XP">
          <Num value={d.xp} min={0} max={10_000_000} onChange={(xp) => updateDetails({ xp })} />
        </Field>
        <Field label="Player">
          <input value={c.playerName} readOnly disabled />
        </Field>
        <Field label="Portrait URL" className="span2">
          <input value={d.portraitUrl} maxLength={500} placeholder="https://…" onChange={(e) => updateDetails({ portraitUrl: e.target.value.trim() })} />
        </Field>
      </div>
      <div className="multiclass">
        <span className="muted small">Multiclass{d.multiclass.length > 0 && ` · total level ${total}`}</span>
        {d.multiclass.map((m, i) => (
          <span key={i} className="mc-row">
            <ChoiceSelect value={m.className} options={CLASSES} homebrew={hb.class} placeholder="Class" onChange={(className) => updateDetails({ multiclass: d.multiclass.map((x, j) => (j === i ? { ...x, className } : x)) })} />
            <Num value={m.level} min={1} max={20} onChange={(level) => updateDetails({ multiclass: d.multiclass.map((x, j) => (j === i ? { ...x, level } : x)) })} />
            <button type="button" className="icon" title="Remove" onClick={() => updateDetails({ multiclass: d.multiclass.filter((_, j) => j !== i) })}>
              ✕
            </button>
          </span>
        ))}
        {d.multiclass.length < 5 && (
          <button type="button" className="small-btn" onClick={() => updateDetails({ multiclass: [...d.multiclass, { className: "", level: 1 }] })}>
            + Class
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ combat
export function CombatSection({ c }: { c: SheetCtx }) {
  const { sheet, d, update, updateDetails, character } = c;
  const init = checkModifier(sheet, { kind: "initiative" });
  const live = character ?? null;
  return (
    <div className="combat-sheet">
      <div className="stat-boxes">
        <Field label="AC">
          <Num value={sheet.ac} min={0} max={40} onChange={(ac) => update({ ac })} />
        </Field>
        <Field label="Initiative">
          <span className="stat-val">
            {formatMod(init)} <RollBtn c={c} title="Roll initiative" onClick={() => checkRoll(c, { kind: "initiative" })} />
          </span>
        </Field>
        <Field label="Init misc">
          <Num value={d.initiativeMisc} min={-10} max={20} onChange={(initiativeMisc) => updateDetails({ initiativeMisc })} title="Added to DEX mod (e.g. Alert +5)" />
        </Field>
        <Field label="Proficiency">
          <Num value={sheet.proficiencyBonus} min={0} max={10} onChange={(proficiencyBonus) => update({ proficiencyBonus })} />
        </Field>
      </div>
      <div className="stat-boxes">
        <Field label="Walk">
          <Num value={sheet.speed} min={0} max={500} onChange={(speed) => update({ speed })} />
        </Field>
        {(["fly", "swim", "climb"] as const).map((k) => (
          <Field key={k} label={cap(k)}>
            <Num value={d.speeds[k]} min={0} max={500} onChange={(v) => updateDetails({ speeds: { ...d.speeds, [k]: v } })} />
          </Field>
        ))}
      </div>
      <div className="hp-block">
        <div className="stat-boxes">
          <Field label="Max HP">
            <Num value={sheet.maxHp} min={1} max={999} onChange={(maxHp) => update({ maxHp, currentHp: Math.min(sheet.currentHp, maxHp) })} />
          </Field>
          <Field label="Current HP">
            <Num value={sheet.currentHp} min={0} max={sheet.maxHp} onChange={(currentHp) => update({ currentHp })} />
          </Field>
          <Field label="Temp HP">
            <Num value={sheet.tempHp} min={0} max={999} onChange={(tempHp) => update({ tempHp })} />
          </Field>
        </div>
        <HpBar hp={sheet.currentHp} maxHp={sheet.maxHp} tempHp={sheet.tempHp} />
        {live && sheet.currentHp === 0 && (
          <div className="death-row">
            <span className="tag down">down</span> Death saves <DeathSaves {...live.deathSaves} />
            <RollBtn c={c} title="Roll a death saving throw" label="🎲 Death save" onClick={() => checkRoll(c, { kind: "death" })} />
          </div>
        )}
      </div>
      <div className="stat-boxes">
        <label className="check-inline">
          <input type="checkbox" checked={d.inspiration} onChange={() => updateDetails({ inspiration: !d.inspiration })} /> Inspiration
        </label>
        <Field label="Exhaustion">
          <select value={d.exhaustion} onChange={(e) => updateDetails({ exhaustion: Number(e.target.value) })}>
            {[0, 1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="passives">
        {(["perception", "investigation", "insight"] as const).map((k) => (
          <span key={k} className="passive">
            <strong>{passiveScore(sheet, k)}</strong> Passive {SKILL_NAMES[k]}
          </span>
        ))}
      </div>
      <div className="hit-dice">
        <span className="muted small">Hit dice</span>
        {d.hitDice.map((h, i) => (
          <span key={i} className="hd-row">
            <select value={h.die} onChange={(e) => updateDetails({ hitDice: d.hitDice.map((x, j) => (j === i ? { ...x, die: Number(e.target.value) } : x)) })}>
              {HIT_DIE_SIDES.map((s) => (
                <option key={s} value={s}>
                  d{s}
                </option>
              ))}
            </select>
            <Num value={h.remaining} min={0} max={h.total} aria-label="Remaining" onChange={(remaining) => updateDetails({ hitDice: d.hitDice.map((x, j) => (j === i ? { ...x, remaining } : x)) })} />
            <span className="muted">/</span>
            <Num value={h.total} min={0} max={20} aria-label="Total" onChange={(total) => updateDetails({ hitDice: d.hitDice.map((x, j) => (j === i ? { ...x, total, remaining: Math.min(x.remaining, total) } : x)) })} />
            <RollBtn c={c} title={`Spend a d${h.die} hit die (roll + CON, heals you)`} label={`🎲 Spend`} onClick={() => void c.spendHitDie(h.die)} />
            <button type="button" className="icon" title="Remove" onClick={() => updateDetails({ hitDice: d.hitDice.filter((_, j) => j !== i) })}>
              ✕
            </button>
          </span>
        ))}
        {d.hitDice.length < 4 && (
          <button type="button" className="small-btn" onClick={() => updateDetails({ hitDice: [...d.hitDice, { die: 8, total: sheet.level, remaining: sheet.level }] })}>
            + Hit dice
          </button>
        )}
      </div>
    </div>
  );
}

export function ConditionsSection({ c }: { c: SheetCtx }) {
  const { d, updateDetails } = c;
  return (
    <div className="conditions">
      {CONDITIONS.map((k) => (
        <label key={k} className={`cond ${d.conditions.includes(k) ? "on" : ""}`}>
          <input type="checkbox" checked={d.conditions.includes(k)} onChange={() => updateDetails({ conditions: toggle(d.conditions, k) })} />
          {cap(k)}
        </label>
      ))}
    </div>
  );
}

// ------------------------------------------------------------ abilities & skills
export function AbilitiesSection({ c }: { c: SheetCtx }) {
  const { sheet, update } = c;
  const setAbility = (a: Ability, v: number) => update({ abilities: { ...sheet.abilities, [a]: v } });
  return (
    <div className="ability-grid">
      {ABILITIES.map((a) => (
        <div key={a} className="ability">
          <span className="abbr" title={ABILITY_NAMES[a]}>
            {a.toUpperCase()}
          </span>
          <Num value={sheet.abilities[a]} min={1} max={30} onChange={(v) => setAbility(a, v)} />
          <span className="mod">
            {formatMod(abilityMod(sheet.abilities[a]))} <RollBtn c={c} title={`${ABILITY_NAMES[a]} check`} onClick={() => checkRoll(c, { kind: "ability", key: a })} />
          </span>
          <label className="save">
            <input type="checkbox" checked={sheet.saveProficiencies.includes(a)} onChange={() => update({ saveProficiencies: toggle(sheet.saveProficiencies, a) })} />
            Save {formatMod(checkModifier(sheet, { kind: "save", key: a }))}
            <RollBtn c={c} title={`${a.toUpperCase()} saving throw`} onClick={() => checkRoll(c, { kind: "save", key: a })} />
          </label>
        </div>
      ))}
    </div>
  );
}

export function SkillsSection({ c }: { c: SheetCtx }) {
  const { sheet, d, update, updateDetails } = c;
  return (
    <>
      <div className="skills-head muted small">
        <span title="Proficient">P</span>
        <span title="Expertise (double proficiency)">E</span>
        <label className="check-inline" title="Jack of All Trades: half proficiency on checks you're not proficient in">
          <input type="checkbox" checked={d.jackOfAllTrades} onChange={() => updateDetails({ jackOfAllTrades: !d.jackOfAllTrades })} /> Jack of All Trades
        </label>
      </div>
      <ul className="skill-list">
        {SKILL_KEYS.map((k: Skill) => (
          <li key={k}>
            <input type="checkbox" title="Proficient" checked={sheet.skillProficiencies.includes(k)} onChange={() => update({ skillProficiencies: toggle(sheet.skillProficiencies, k) })} />
            <input
              type="checkbox"
              title="Expertise"
              checked={d.expertise.includes(k)}
              onChange={() => {
                const on = !d.expertise.includes(k);
                updateDetails({ expertise: toggle(d.expertise, k) });
                if (on && !sheet.skillProficiencies.includes(k)) update({ skillProficiencies: [...sheet.skillProficiencies, k] });
              }}
            />
            <span className="mod">{formatMod(checkModifier(sheet, { kind: "skill", key: k }))}</span>
            <span className="skill-name">
              {SKILL_NAMES[k]} <span className="muted small">({SKILLS[k].toUpperCase()})</span>
            </span>
            <RollBtn c={c} title={`${SKILL_NAMES[k]} check`} onClick={() => checkRoll(c, { kind: "skill", key: k })} />
          </li>
        ))}
      </ul>
    </>
  );
}

export function ProficienciesSection({ c }: { c: SheetCtx }) {
  const { d, updateDetails } = c;
  const keys = [
    ["armor", "Armor", "Light, medium, shields"],
    ["weapons", "Weapons", "Simple, martial"],
    ["tools", "Tools", "Thieves' tools, smith's tools"],
    ["languages", "Languages", "Common, Dwarvish"],
  ] as const;
  return (
    <div className="prof-grid">
      {keys.map(([k, label, ph]) => (
        <Field key={k} label={label}>
          <textarea rows={2} maxLength={2000} placeholder={ph} value={d.proficiencies[k]} onChange={(e) => updateDetails({ proficiencies: { ...d.proficiencies, [k]: e.target.value } })} />
        </Field>
      ))}
    </div>
  );
}

// ------------------------------------------------------------ attacks
export function AttacksSection({ c }: { c: SheetCtx }) {
  const { sheet, d, updateDetails } = c;
  const [open, setOpen] = useState<string | null>(null);
  const set = (id: string, p: Partial<Attack>) => updateDetails({ attacks: patchItem(d.attacks, id, p) });
  const add = () => {
    const a: Attack = { id: newId(), name: "", ability: "str", proficient: true, attackBonusOverride: null, damageDice: "1d6", damageType: "", addModToDamage: true, range: "", notes: "" };
    updateDetails({ attacks: [...d.attacks, a] });
    setOpen(a.id);
  };
  const rollAttack = (a: Attack) => c.roll({ sides: 20, count: 1, modifier: attackBonus(sheet, a), label: `${a.name || "Attack"} attack` }, true);
  const rollDamage = (a: Attack) => {
    const spec = parseFormula(damageFormula(sheet, a) ?? "");
    if (spec) void c.roll({ sides: spec.sides, count: spec.count, modifier: spec.modifier, label: `${a.name || "Attack"} damage${a.damageType ? ` (${a.damageType})` : ""}` });
  };
  return (
    <>
      <ul className="attack-list">
        {d.attacks.map((a) => {
          const dmg = damageFormula(sheet, a);
          return (
            <li key={a.id}>
              <div className="attack-row">
                <input className="name" value={a.name} maxLength={80} placeholder="Longsword" onChange={(e) => set(a.id, { name: e.target.value })} />
                <span className="atk-bonus" title="Attack bonus">
                  {formatMod(attackBonus(sheet, a))}
                  <RollBtn c={c} title="Roll attack (d20, uses advantage toggle)" onClick={() => void rollAttack(a)} />
                </span>
                <span className="atk-dmg" title="Damage">
                  {dmg ?? (a.damageDice || "—")} {a.damageType && <span className="muted small">{a.damageType}</span>}
                  {dmg && <RollBtn c={c} title="Roll damage" onClick={() => rollDamage(a)} />}
                </span>
                <button type="button" className="icon" title="Edit details" onClick={() => setOpen(open === a.id ? null : a.id)}>
                  {open === a.id ? "▴" : "▾"}
                </button>
                <button type="button" className="icon" title="Remove" onClick={() => updateDetails({ attacks: d.attacks.filter((x) => x.id !== a.id) })}>
                  ✕
                </button>
              </div>
              {(open === a.id || a.notes || a.range) && open !== a.id && (
                <div className="muted small attack-notes">{[a.range, a.notes].filter(Boolean).join(" · ")}</div>
              )}
              {open === a.id && (
                <div className="attack-edit">
                  <Field label="Ability">
                    <select value={a.ability} onChange={(e) => set(a.id, { ability: e.target.value as Attack["ability"] })}>
                      {ABILITIES.map((x) => (
                        <option key={x} value={x}>
                          {x.toUpperCase()}
                        </option>
                      ))}
                      <option value="none">none</option>
                    </select>
                  </Field>
                  <label className="check-inline">
                    <input type="checkbox" checked={a.proficient} onChange={() => set(a.id, { proficient: !a.proficient })} /> Proficient
                  </label>
                  <Field label="Bonus override">
                    <OptNum value={a.attackBonusOverride} placeholder="auto" onChange={(v) => set(a.id, { attackBonusOverride: v })} />
                  </Field>
                  <Field label="Damage dice">
                    <input value={a.damageDice} maxLength={30} placeholder="1d8" onChange={(e) => set(a.id, { damageDice: e.target.value })} />
                  </Field>
                  <label className="check-inline">
                    <input type="checkbox" checked={a.addModToDamage} onChange={() => set(a.id, { addModToDamage: !a.addModToDamage })} /> + mod to damage
                  </label>
                  <Field label="Damage type">
                    <input value={a.damageType} maxLength={40} placeholder="slashing" onChange={(e) => set(a.id, { damageType: e.target.value })} />
                  </Field>
                  <Field label="Range">
                    <input value={a.range} maxLength={40} placeholder="5 ft / 80/320" onChange={(e) => set(a.id, { range: e.target.value })} />
                  </Field>
                  <Field label="Notes" className="span-all">
                    <input value={a.notes} maxLength={300} placeholder="Versatile (1d10), finesse…" onChange={(e) => set(a.id, { notes: e.target.value })} />
                  </Field>
                </div>
              )}
            </li>
          );
        })}
        {d.attacks.length === 0 && <li className="muted">No attacks yet.</li>}
      </ul>
      <button type="button" className="small-btn" onClick={add}>
        + Attack or cantrip
      </button>
    </>
  );
}

// ------------------------------------------------------------ spells
export function SpellsSection({ c }: { c: SheetCtx }) {
  const { sheet, d, updateDetails } = c;
  const sc = d.spellcasting;
  const setSc = (p: Partial<CharacterDetails["spellcasting"]>) => updateDetails({ spellcasting: { ...sc, ...p } });
  const setSpell = (id: string, p: Partial<Spell>) => setSc({ spells: patchItem(sc.spells, id, p) });
  const [open, setOpen] = useState<string | null>(null);
  const [addLevel, setAddLevel] = useState(0);
  const dc = spellSaveDc(sheet);
  const atk = spellAttackBonus(sheet);
  const add = () => {
    const s: Spell = { id: newId(), level: addLevel, name: "", prepared: addLevel === 0, school: "", castingTime: "1 action", range: "", components: "", duration: "Instantaneous", concentration: false, ritual: false, description: "" };
    setSc({ spells: [...sc.spells, s] });
    setOpen(s.id);
  };
  const cast = (s: Spell) => {
    const next = spendSlot(d, s.level);
    if (next) updateDetails({ spellcasting: next.spellcasting });
    else window.alert(`No level ${s.level}+ slots left for ${s.name || "that spell"}.`);
  };
  const byLevel = Array.from({ length: 10 }, (_, l) => sc.spells.filter((s) => s.level === l)).map((list, l) => ({ l, list }));
  return (
    <>
      <div className="stat-boxes">
        <Field label="Ability">
          <select value={sc.ability ?? ""} onChange={(e) => setSc({ ability: (e.target.value || null) as Ability | null })}>
            <option value="">— none —</option>
            {ABILITIES.map((x) => (
              <option key={x} value={x}>
                {ABILITY_NAMES[x]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Save DC">
          <span className="stat-val">{dc ?? "—"}</span>
        </Field>
        <Field label="Spell attack">
          <span className="stat-val">
            {atk === null ? "—" : formatMod(atk)}
            {atk !== null && <RollBtn c={c} title="Spell attack roll" onClick={() => void c.roll({ sides: 20, count: 1, modifier: atk, label: "Spell attack" }, true)} />}
          </span>
        </Field>
      </div>
      <div className="slots">
        {sc.slots.map((s, i) => (
          <div key={i} className={`slot ${s.max ? "" : "empty"}`}>
            <span className="muted small">{ORD[i + 1]}</span>
            <Num value={s.max} min={0} max={9} aria-label={`${ORD[i + 1]} level slots`} onChange={(max) => setSc({ slots: sc.slots.map((x, j) => (j === i ? { max, used: Math.min(x.used, max) } : x)) })} />
            <Pips max={s.max} used={s.used} onChange={(used) => setSc({ slots: sc.slots.map((x, j) => (j === i ? { ...x, used } : x)) })} />
          </div>
        ))}
        <div className="slot pact">
          <span className="muted small">Pact</span>
          <Num value={sc.pact.max} min={0} max={4} aria-label="Pact slots" onChange={(max) => setSc({ pact: { ...sc.pact, max, used: Math.min(sc.pact.used, max) } })} />
          <span className="muted small">lvl</span>
          <Num value={sc.pact.level} min={1} max={5} aria-label="Pact slot level" onChange={(level) => setSc({ pact: { ...sc.pact, level } })} />
          <Pips max={sc.pact.max} used={sc.pact.used} onChange={(used) => setSc({ pact: { ...sc.pact, used } })} />
        </div>
      </div>
      <div className="spell-list scroll-area">
        {byLevel
          .filter(({ list }) => list.length)
          .map(({ l, list }) => (
            <div key={l} className="spell-level">
              <h4>{l === 0 ? "Cantrips" : `${ORD[l]} level`}</h4>
              <ul>
                {list.map((s) => (
                  <li key={s.id}>
                    <div className="spell-row">
                      {l > 0 ? (
                        <input type="checkbox" title="Prepared" checked={s.prepared} onChange={() => setSpell(s.id, { prepared: !s.prepared })} />
                      ) : (
                        <span className="pad" />
                      )}
                      <input className="name" value={s.name} maxLength={80} placeholder="Spell name" onChange={(e) => setSpell(s.id, { name: e.target.value })} />
                      <span className="spell-meta muted small">
                        {[s.castingTime, s.range, s.concentration && "C", s.ritual && "R"].filter(Boolean).join(" · ")}
                      </span>
                      {l > 0 && (
                        <button type="button" className="small-btn" title="Cast: spend a slot of this level (or higher pact slot)" onClick={() => cast(s)}>
                          Cast
                        </button>
                      )}
                      <button type="button" className="icon" title="Details" onClick={() => setOpen(open === s.id ? null : s.id)}>
                        {open === s.id ? "▴" : "▾"}
                      </button>
                      <button type="button" className="icon" title="Remove" onClick={() => setSc({ spells: sc.spells.filter((x) => x.id !== s.id) })}>
                        ✕
                      </button>
                    </div>
                    {open === s.id && (
                      <div className="spell-edit">
                        <Field label="Level">
                          <select value={s.level} onChange={(e) => setSpell(s.id, { level: Number(e.target.value) })}>
                            {ORD.map((o, i) => (
                              <option key={i} value={i}>
                                {i === 0 ? "Cantrip" : o}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="School">
                          <input value={s.school} maxLength={40} placeholder="Evocation" onChange={(e) => setSpell(s.id, { school: e.target.value })} />
                        </Field>
                        <Field label="Casting time">
                          <input value={s.castingTime} maxLength={40} onChange={(e) => setSpell(s.id, { castingTime: e.target.value })} />
                        </Field>
                        <Field label="Range">
                          <input value={s.range} maxLength={40} placeholder="120 ft" onChange={(e) => setSpell(s.id, { range: e.target.value })} />
                        </Field>
                        <Field label="Components">
                          <input value={s.components} maxLength={120} placeholder="V, S, M (a bit of bat fur)" onChange={(e) => setSpell(s.id, { components: e.target.value })} />
                        </Field>
                        <Field label="Duration">
                          <input value={s.duration} maxLength={60} onChange={(e) => setSpell(s.id, { duration: e.target.value })} />
                        </Field>
                        <label className="check-inline">
                          <input type="checkbox" checked={s.concentration} onChange={() => setSpell(s.id, { concentration: !s.concentration })} /> Concentration
                        </label>
                        <label className="check-inline">
                          <input type="checkbox" checked={s.ritual} onChange={() => setSpell(s.id, { ritual: !s.ritual })} /> Ritual
                        </label>
                        <Field label="Description" className="span-all">
                          <textarea rows={4} maxLength={5000} value={s.description} onChange={(e) => setSpell(s.id, { description: e.target.value })} />
                        </Field>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        {sc.spells.length === 0 && <p className="muted">No spells yet.</p>}
      </div>
      <div className="add-row">
        <select value={addLevel} onChange={(e) => setAddLevel(Number(e.target.value))} aria-label="Spell level">
          {ORD.map((o, i) => (
            <option key={i} value={i}>
              {i === 0 ? "Cantrip" : `${o} level`}
            </option>
          ))}
        </select>
        <button type="button" className="small-btn" onClick={add}>
          + Spell
        </button>
      </div>
    </>
  );
}

// ------------------------------------------------------------ features
export function FeaturesSection({ c }: { c: SheetCtx }) {
  const { d, updateDetails } = c;
  const [open, setOpen] = useState<string | null>(null);
  const set = (id: string, p: Partial<Feature>) => updateDetails({ features: patchItem(d.features, id, p) });
  const add = () => {
    const f: Feature = { id: newId(), kind: "class", name: "", description: "", maxUses: null, used: 0, recharge: "long" };
    updateDetails({ features: [...d.features, f] });
    setOpen(f.id);
  };
  const kinds: Array<[Feature["kind"], string]> = [["class", "Class features"], ["race", "Racial traits"], ["feat", "Feats"]];
  return (
    <>
      {kinds.map(([kind, title]) => {
        const list = d.features.filter((f) => f.kind === kind);
        if (!list.length) return null;
        return (
          <div key={kind} className="feature-group">
            <h4>{title}</h4>
            <ul className="feature-list">
              {list.map((f) => (
                <li key={f.id}>
                  <div className="feature-row">
                    <input className="name" value={f.name} maxLength={80} placeholder="Second Wind" onChange={(e) => set(f.id, { name: e.target.value })} />
                    {f.maxUses ? (
                      <span className="uses">
                        <Pips max={f.maxUses} used={f.used} onChange={(used) => set(f.id, { used })} />
                        <span className="muted small">{f.recharge === "none" ? "" : `${f.recharge} rest`}</span>
                      </span>
                    ) : null}
                    <button type="button" className="icon" title="Details" onClick={() => setOpen(open === f.id ? null : f.id)}>
                      {open === f.id ? "▴" : "▾"}
                    </button>
                    <button type="button" className="icon" title="Remove" onClick={() => updateDetails({ features: d.features.filter((x) => x.id !== f.id) })}>
                      ✕
                    </button>
                  </div>
                  {open !== f.id && f.description && <p className="feature-desc muted small">{f.description}</p>}
                  {open === f.id && (
                    <div className="feature-edit">
                      <Field label="Type">
                        <select value={f.kind} onChange={(e) => set(f.id, { kind: e.target.value as Feature["kind"] })}>
                          <option value="class">Class</option>
                          <option value="race">Race</option>
                          <option value="feat">Feat</option>
                        </select>
                      </Field>
                      <Field label="Uses">
                        <OptNum value={f.maxUses} min={1} max={20} placeholder="—" title="Leave empty if unlimited" onChange={(maxUses) => set(f.id, { maxUses, used: Math.min(f.used, maxUses ?? 0) })} />
                      </Field>
                      <Field label="Recharge">
                        <select value={f.recharge} onChange={(e) => set(f.id, { recharge: e.target.value as Recharge })}>
                          <option value="short">Short rest</option>
                          <option value="long">Long rest</option>
                          <option value="none">Never</option>
                        </select>
                      </Field>
                      <Field label="Description" className="span-all">
                        <textarea rows={3} maxLength={5000} value={f.description} onChange={(e) => set(f.id, { description: e.target.value })} />
                      </Field>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {d.features.length === 0 && <p className="muted">No features yet.</p>}
      <button type="button" className="small-btn" onClick={add}>
        + Feature or trait
      </button>
    </>
  );
}

// ------------------------------------------------------------ equipment
export function EquipmentSection({ c }: { c: SheetCtx }) {
  const { sheet, d, updateDetails } = c;
  const set = (id: string, p: Partial<Item>) => updateDetails({ equipment: patchItem(d.equipment, id, p) });
  const weight = totalWeight(d.equipment);
  const capacity = carryingCapacity(sheet.abilities.str);
  const attuned = attunedCount(d.equipment);
  const add = () => updateDetails({ equipment: [...d.equipment, { id: newId(), name: "", qty: 1, weight: 0, equipped: false, attuned: false, notes: "" }] });
  return (
    <>
      <div className="equip-table-wrap">
        <table className="equip-table">
          <thead>
            <tr>
              <th title="Equipped">Eq</th>
              <th title="Attuned">Att</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Wt (lb)</th>
              <th>Notes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.equipment.map((it) => (
              <tr key={it.id}>
                <td>
                  <input type="checkbox" checked={it.equipped} onChange={() => set(it.id, { equipped: !it.equipped })} />
                </td>
                <td>
                  <input type="checkbox" checked={it.attuned} onChange={() => set(it.id, { attuned: !it.attuned })} />
                </td>
                <td>
                  <input className="name" value={it.name} maxLength={120} placeholder="Item" onChange={(e) => set(it.id, { name: e.target.value })} />
                </td>
                <td>
                  <Num value={it.qty} min={0} max={9999} onChange={(qty) => set(it.id, { qty })} />
                </td>
                <td>
                  <Num value={it.weight} min={0} max={9999} step={0.01} onChange={(weight) => set(it.id, { weight })} />
                </td>
                <td>
                  <input value={it.notes} maxLength={300} onChange={(e) => set(it.id, { notes: e.target.value })} />
                </td>
                <td>
                  <button type="button" className="icon" title="Remove" onClick={() => updateDetails({ equipment: d.equipment.filter((x) => x.id !== it.id) })}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
            {d.equipment.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No equipment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="equip-foot">
        <button type="button" className="small-btn" onClick={add}>
          + Item
        </button>
        <span className={weight > capacity ? "warn" : ""}>
          Weight {Math.round(weight * 100) / 100} / {capacity} lb
        </span>
        <span className={attuned > MAX_ATTUNEMENT ? "warn" : ""}>
          Attuned {attuned}/{MAX_ATTUNEMENT}
        </span>
      </div>
      <div className="currency">
        {(["cp", "sp", "ep", "gp", "pp"] as const).map((k) => (
          <Field key={k} label={k.toUpperCase()}>
            <Num value={d.currency[k]} min={0} max={10_000_000} onChange={(v) => updateDetails({ currency: { ...d.currency, [k]: v } })} />
          </Field>
        ))}
      </div>
    </>
  );
}

// ------------------------------------------------------------ personality & notes
export function PersonalitySection({ c }: { c: SheetCtx }) {
  const { d, updateDetails } = c;
  const keys = [
    ["traits", "Personality traits", 2],
    ["ideals", "Ideals", 2],
    ["bonds", "Bonds", 2],
    ["flaws", "Flaws", 2],
    ["appearance", "Appearance", 3],
    ["allies", "Allies & organizations", 3],
    ["backstory", "Backstory", 6],
  ] as const;
  return (
    <div className="prof-grid">
      {keys.map(([k, label, rows]) => (
        <Field key={k} label={label} className={k === "backstory" ? "span-all" : ""}>
          <textarea rows={rows} maxLength={k === "backstory" ? 20000 : 4000} value={d.personality[k]} onChange={(e) => updateDetails({ personality: { ...d.personality, [k]: e.target.value } })} />
        </Field>
      ))}
    </div>
  );
}

export function NotesSection({ c }: { c: SheetCtx }) {
  return <textarea rows={8} maxLength={20000} value={c.sheet.notes} onChange={(e) => c.update({ notes: e.target.value })} placeholder="Session notes, quests, NPCs…" />;
}

export function RollModeBar({ c }: { c: SheetCtx }) {
  return c.canAct ? (
    <span className="mode-bar">
      <span className="muted small">d20 rolls:</span>
      <ModeToggle mode={c.mode} onChange={c.setMode} />
    </span>
  ) : null;
}
